import React, { useLayoutEffect } from 'react'
import useExperienceNavVisibility from './useExperienceNavVisibility'
import useExperienceNavActions from './useExperienceNavActions'
import { useExperienceNav } from './ExperienceNavContext'
import useLayoutVisibility from '../../hooks/useLayoutVisibility'
import SharedNavigationSurface from './SharedNavigationSurface'
import { experienceNavPropTypes } from './navigationItemPropType'
import { buildNavigationIndex } from './navigationModel'
import useExperienceNavCssVars from './useExperienceNavCssVars'

// The shared navigation card and its handle, in every mode: WEB, STATION, and
// the Together WebView - where the kids return is an exit row below the
// experience's own rows (the STATION convention, with Leave Kiosk) and the
// handle keeps its WEB top-start corner.
// Rendered by ConditionalHeader — never mount it directly from an experience.
export default function ExperienceNavControls({
  navHomePath = null,
  navAnalyticsPrefix = null,
  navItems = [],
  navSectionLabel = null,
  navParent = null,
  navShowStepButtons = true,
}) {
  const { effectiveShowHeader } = useLayoutVisibility()
  const { isStation, kidsReturnTo, showHome, showKidsReturn, showLeaveKioskAction } =
    useExperienceNavVisibility(navHomePath)
  const nav = useExperienceNav()
  const registration = nav?.back ?? null
  // An experience that declares its rows from inside itself (ExperienceNavItems) overrides
  // the props, which are built at the shell and so cannot see data fetched below it.
  // Nothing registers for experiences that pass props, so they take the props untouched.
  const itemsRegistration = nav?.navItems ?? null
  const index = buildNavigationIndex({
    navItems: itemsRegistration?.items ?? navItems,
    navParent: itemsRegistration?.parent ?? navParent,
    registration,
  })
  // An experience that opted out keeps the card and its handle - only the
  // top-end stepper goes, and with it the top-END corner it reserved.
  const stepRing = navShowStepButtons ? index.stepRing : null

  // A registration counts on its own, no navHomePath required: it can only
  // come from a mounted ExperienceNavBack, i.e. a migrated experience - e.g.
  // heritage's signed-out guests keep a labelled Back on category detail even
  // though their navHomePath is null (no Home level to go up to). Non-migrated
  // experiences have no provider, so nothing ever registers for them.
  const hasControls =
    showLeaveKioskAction ||
    showKidsReturn ||
    showHome ||
    Boolean(registration) ||
    index.hasItems ||
    index.hasDeclaredParent

  const { leaveKiosk, reportedBack, selectParent, selectNavItem, goHome, kidsReturn } = useExperienceNavActions({
    registration,
    navParent: index.hasDeclaredParent ? navParent : null,
    kidsReturnTo,
    analyticsPrefix: navAnalyticsPrefix,
  })

  // Is anything sitting in the top-start corner, where a page's own
  // top-anchored content goes? The WEB corner trigger is (in the Together
  // WebView too). STATION's edge tab is centered against the side instead,
  // clear of the top entirely.
  const occupiesTopCorner = hasControls && !isStation
  // And the top-END corner: only the stepper ever sits there, and only outside
  // STATION, which centers it against the side, mirroring the edge tab.
  const occupiesTopEndCorner = !isStation && Boolean(stepRing)
  useExperienceNavCssVars({ isStation, hasControls, occupiesTopCorner, occupiesTopEndCorner })

  // Publish the rendered state into the provider (useExperienceNavRendered)
  // for experiences that react to visible shared navigation. Layout effect so
  // consumers reflow before paint; reset on unmount.
  const setControlsRendered = nav?.setControlsRendered
  useLayoutEffect(() => {
    if (!setControlsRendered) return undefined
    setControlsRendered(hasControls)
    return () => setControlsRendered(false)
  }, [setControlsRendered, hasControls])

  // And whether they took the top-start corner (WEB) or the side (STATION) -
  // the distinction useExperienceNavCssVars draws for --experience-nav-
  // clearance, published here as a boolean (useExperienceNavOccupiesTopCorner)
  // for content that reserves room beside the controls rather than below them.
  const setOccupiesTopCorner = nav?.setOccupiesTopCorner
  useLayoutEffect(() => {
    if (!setOccupiesTopCorner) return undefined
    setOccupiesTopCorner(occupiesTopCorner)
    return () => setOccupiesTopCorner(false)
  }, [setOccupiesTopCorner, occupiesTopCorner])

  if (!hasControls) return null

  return (
    <SharedNavigationSurface
      isStation={isStation}
      headerVisible={effectiveShowHeader}
      registration={registration}
      showBack={index.showBack}
      showHome={showHome}
      showKidsReturn={showKidsReturn}
      showLeaveKiosk={showLeaveKioskAction}
      parentItem={index.parent}
      sectionLabel={navSectionLabel}
      navItems={index.items}
      stepRing={stepRing}
      onBack={reportedBack}
      onSelectParent={selectParent}
      onSelectNavItem={selectNavItem}
      onHome={goHome}
      onKidsReturn={kidsReturn}
      onLeaveKiosk={leaveKiosk}
    />
  )
}

ExperienceNavControls.propTypes = experienceNavPropTypes
